import * as nifti from 'nifti-reader-js'
import pako from 'pako'

export interface NiftiVolume {
  data: Float32Array
  dims: [number, number, number]   // (dimX, dimY, dimZ) — sitk's GetSize order
  spacing: [number, number, number]
}

export async function fetchNifti(url: string): Promise<NiftiVolume> {
  const res = await fetch(url)
  if (!res.ok) throw new Error(`fetch ${url} → ${res.status}`)
  let buf: ArrayBuffer = await res.arrayBuffer()
  // Most of our files are .nii.gz — try gunzip first.
  if (nifti.isCompressed(buf)) {
    buf = nifti.decompress(buf) as ArrayBuffer
  } else {
    // Some servers might transparently decompress; allow either.
    try {
      const u8 = new Uint8Array(buf)
      if (u8[0] === 0x1f && u8[1] === 0x8b) {
        buf = pako.ungzip(u8).buffer
      }
    } catch {
      // ignore — fall through to raw parse
    }
  }
  if (!nifti.isNIFTI(buf)) {
    throw new Error('not a NIfTI file')
  }
  const header = nifti.readHeader(buf)
  if (!header) throw new Error('bad NIfTI header')
  const image = nifti.readImage(header, buf)
  const dims: [number, number, number] = [
    header.dims[1],
    header.dims[2],
    header.dims[3],
  ]
  const spacing: [number, number, number] = [
    header.pixDims[1],
    header.pixDims[2],
    header.pixDims[3],
  ]
  const data = toFloat32(image, header.datatypeCode)
  return { data, dims, spacing }
}

function toFloat32(buf: ArrayBuffer, code: number): Float32Array {
  // NIfTI datatype codes: 2=u8, 4=i16, 8=i32, 16=f32, 64=f64, 256=i8, 512=u16, 768=u32
  switch (code) {
    case 2:
      return Float32Array.from(new Uint8Array(buf))
    case 4:
      return Float32Array.from(new Int16Array(buf))
    case 8:
      return Float32Array.from(new Int32Array(buf))
    case 16:
      return new Float32Array(buf)
    case 64:
      return Float32Array.from(new Float64Array(buf))
    case 256:
      return Float32Array.from(new Int8Array(buf))
    case 512:
      return Float32Array.from(new Uint16Array(buf))
    case 768:
      return Float32Array.from(new Uint32Array(buf))
    default:
      throw new Error(`unsupported NIfTI datatype code ${code}`)
  }
}

/**
 * Extract a single Z slice. The NIfTI memory layout from nifti-reader-js is
 * Fortran order on (X, Y, Z) — i.e. linear index = x + y*dimX + z*dimX*dimY.
 *
 * Returns an array of length dimX * dimY in row-major (Y, X) order so it can
 * be written directly into a canvas's ImageData.
 */
export function extractAxialSlice(
  vol: NiftiVolume,
  z: number,
): { width: number; height: number; data: Float32Array } {
  const [dx, dy, dz] = vol.dims
  if (z < 0 || z >= dz) {
    throw new Error(`slice index out of range: ${z}`)
  }
  const sliceSize = dx * dy
  const offset = z * sliceSize
  const out = new Float32Array(sliceSize)
  for (let y = 0; y < dy; y++) {
    for (let x = 0; x < dx; x++) {
      out[y * dx + x] = vol.data[offset + x + y * dx]
    }
  }
  return { width: dx, height: dy, data: out }
}

export function percentileWindow(
  vol: NiftiVolume,
  lo = 0.5,
  hi = 99.5,
): { vmin: number; vmax: number } {
  // Sample-based percentile to avoid sorting all voxels.
  const n = vol.data.length
  const step = Math.max(1, Math.floor(n / 50000))
  const samples: number[] = []
  for (let i = 0; i < n; i += step) samples.push(vol.data[i])
  samples.sort((a, b) => a - b)
  const at = (p: number) => samples[Math.min(samples.length - 1, Math.floor((p / 100) * samples.length))]
  return { vmin: at(lo), vmax: at(hi) }
}
